import React, { useMemo, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ContextUser } from '../../../providers/ContextUser';

const Element = styled.ul`
  min-height: 36px;
  min-width: 350px;
  display: flex;
  align-items: center;
  padding: 5px;
  margin: 0;
  list-style: none;

  li {
    display: flex;
    align-items: center;
    margin-right: 8px;

    a {
      color: #6b7280;
      text-decoration: none;
      transition: all 0.3s ease;
      font-weight: 500;
      font-size: 14px;
      padding: 6px 12px;
      border-radius: 6px;
      position: relative;

      &:hover {
        color: #1a1a2e;
        background: rgba(26, 26, 46, 0.05);
      }

      &[href=''] {
        text-decoration: none;
      }
    }

    &:after {
      content: '›';
      margin-left: 8px;
      color: #d1d5db;
      font-size: 16px;
      font-weight: 300;
    }

    &:last-child {
      margin-right: 0;

      &:after {
        content: '';
      }

      a {
        color: #1a1a2e;
        font-weight: 600;
        background: rgba(26, 26, 46, 0.08);
        
        &:hover {
          background: rgba(26, 26, 46, 0.12);
        }
      }
    }
  }
`;

const DisabledCrumb = styled.span`
  color: #9ca3af;
  font-weight: 500;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: default;
`;

const CurrentCrumb = styled.span`
  color: #1a1a2e;
  font-weight: 600;
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 6px;
  background: rgba(26, 26, 46, 0.08);
`;

const Breadcrumbs = ({ path }) => {
  const { userHierarchy, currentOrganization, currentUser } = useContext(ContextUser);
  const location = useLocation();

  const urls = {
    admin: '/',
    org: '/prefeitura',
    branch: '/prefeitura/secretaria',
    unit: '/prefeitura/secretaria/unidade',
    department: '/prefeitura/secretaria/unidade/departamento',
  };

  const getUrl = (item) => {
    if (item.key !== 'admin') {
      return `${urls[item?.key]}/${item.id}`;
    }
    return '/';
  };

  // Monta trilha completa combinando props, contexto e URL
  const augmentedPath = useMemo(() => {
    const byKey = Object.fromEntries((path || []).map(i => [i.key, i]));
    const result = [];

    // Admin: exibir somente para papel ADMIN
    if (currentUser?.role === 'ADMIN') {
      result.push(byKey.admin || { id: '/', label: 'admin', key: 'admin', path: '/' });
    }

    // Org
    const orgItem = byKey.org || (currentOrganization?.id && {
      id: currentOrganization.id,
      label: currentOrganization.name,
      key: 'org',
      path: `/prefeitura/${currentOrganization.id}`
    }) || (userHierarchy?.organizationId && {
      id: userHierarchy.organizationId,
      label: userHierarchy.organization?.name,
      key: 'org',
      path: `/prefeitura/${userHierarchy.organizationId}`
    });
    if (orgItem) result.push(orgItem);

    // Detecta nível atual pela URL
    const pathname = location.pathname.endsWith('/') ? location.pathname : location.pathname + '/';
    const wantDepartment = pathname.includes('/prefeitura/secretaria/unidade/departamento/');
    const wantUnit = pathname.includes('/prefeitura/secretaria/unidade/');
    const wantBranch = pathname.includes('/prefeitura/secretaria/');

    // Branch
    if (wantBranch && (userHierarchy?.branchId || byKey.branch)) {
      const branchItem = byKey.branch || {
        id: userHierarchy?.branchId,
        label: userHierarchy?.branch?.name,
        key: 'branch',
        path: userHierarchy?.branchId ? `/prefeitura/secretaria/${userHierarchy.branchId}` : urls.branch
      };
      if (branchItem.id) result.push(branchItem);
    }

    // Unit
    if (wantUnit && (userHierarchy?.unitId || byKey.unit)) {
      const unitItem = byKey.unit || {
        id: userHierarchy?.unitId,
        label: userHierarchy?.unit?.name,
        key: 'unit',
        path: userHierarchy?.unitId ? `/prefeitura/secretaria/unidade/${userHierarchy.unitId}` : urls.unit
      };
      if (unitItem.id) result.push(unitItem);
    }

    // Department
    if (wantDepartment && (userHierarchy?.departmentId || byKey.department)) {
      const deptItem = byKey.department || {
        id: userHierarchy?.departmentId,
        label: userHierarchy?.department?.name,
        key: 'department',
        path: userHierarchy?.departmentId ? `/prefeitura/secretaria/unidade/departamento/${userHierarchy.departmentId}` : urls.department
      };
      if (deptItem.id) result.push(deptItem);
    }

    // Page (se presente)
    if (byKey.page) result.push(byKey.page);

    // Deduplica por key mantendo a primeira ocorrência
    const seen = new Set();
    return result.filter(i => {
      if (!i) return false;
      if (seen.has(i.key)) return false;
      seen.add(i.key);
      return true;
    });
  }, [path, userHierarchy, currentOrganization, location.pathname]);

  // Regras de clique por papel
  const roleClickableKeysMap = {
    ADMIN: ['admin', 'org', 'branch', 'unit'],
    ACCESS_STAFF: ['admin', 'org', 'branch', 'unit'],
    ACCESS_ORG_LEVEL: ['org', 'branch', 'unit'],
    ACCESS_BRANCH_LEVEL: ['branch', 'unit'],
    ACCESS_UNIT_LEVEL: ['unit'],
    ACCESS_DEPARTMENT_LEVEL: [],
  };
  const clickableKeys = roleClickableKeysMap[currentUser?.role] || [];

  return (
    <Element>
      {augmentedPath.map((item, idx) => {
        const isLast = idx === augmentedPath.length - 1;
        const canClick = clickableKeys.includes(item.key) && !isLast;
        const shouldDisable = !canClick;
        return (
          <li key={item.label}>
            {isLast ? (
              <CurrentCrumb aria-current='page'>
                <span style={{ textTransform: 'capitalize' }}>{item.label}</span>
              </CurrentCrumb>
            ) : shouldDisable ? (
              <DisabledCrumb aria-disabled='true'>
                <span style={{ textTransform: 'capitalize' }}>{item.label}</span>
              </DisabledCrumb>
            ) : (
              <Link to={getUrl(item)}>
                <span style={{ textTransform: 'capitalize' }}>{item.label}</span>
              </Link>
            )}
          </li>
        );
      })}
    </Element>
  );
};

export default Breadcrumbs;
